import { useEffect, ReactNode } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface LanguageRouteSyncProps {
  children: ReactNode;
}

/**
 * Component to sync URL language parameter with i18n
 * Handles language changes from language switcher
 */
const LanguageRouteSync = ({ children }: LanguageRouteSyncProps) => {
  const { lang } = useParams<{ lang?: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle language changes from language switcher
  useEffect(() => {
    const handleLanguageChange = (newLang: string) => {
      // Only update URL if the new language differs from URL param
      if (lang && newLang !== lang) {
        // Get current path without language prefix
        const pathParts = location.pathname.split('/').filter(Boolean);
        const pathWithoutLang = '/' + pathParts.slice(1).join('/');
        
        // Navigate to new language URL
        if (pathWithoutLang && pathWithoutLang !== '/') {
          navigate(`/${newLang}${pathWithoutLang}`, { replace: true });
        } else {
          navigate(`/${newLang}/dashboard`, { replace: true });
        }
      }
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [lang, i18n, location.pathname, navigate]);

  return <>{children}</>;
};

export default LanguageRouteSync;
